/** @jsx jsx */
import { useTranslation } from 'react-i18next';
import { jsx } from '@emotion/core';
import styled from '@emotion/styled';
import { Paper, Typography, useTheme } from '@material-ui/core';

import { ICustomTheme } from 'interfaces';
import iconError from 'assets/icons/iconError.svg';

const ResponsiveContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
});

const WarningText = styled(Typography)(
  ({ theme, isMobile }: { theme: ICustomTheme; isMobile: boolean }) => ({
    marginTop: theme.spacing(3),
    color: theme.colors.paletteFromBO.main,
    fontSize: isMobile ? 25 : 48,
    fontWeight: theme.typography.fontWeightBold,
  }),
);

const ErrorText = styled(Typography)(
  ({ theme, isMobile }: { theme: ICustomTheme; isMobile: boolean }) => ({
    color: theme.colors.grey.dark,
    fontSize: isMobile ? 18 : 28,
    textAlign: 'center',
    fontWeight: theme.typography.fontWeightBold,
  }),
);

const ActionText = styled(Typography)(
  ({ theme, isMobile }: { theme: ICustomTheme; isMobile: boolean }) => ({
    color: theme.colors.grey.dark,
    fontSize: isMobile ? theme.typography.body2.fontSize : 22,
    textAlign: 'center',
    fontWeight: theme.typography.fontWeightMedium,
  }),
);

const ImgMobile = styled.img(({ theme }: { theme: ICustomTheme }) => ({
  marginTop: theme.spacing(5),
}));

const ImgDesktop = styled.img(({ theme }: { theme: ICustomTheme }) => ({
  marginLeft: theme.spacing(6),
  marginTop: theme.spacing(5),
}));

export default function ErrorView({
  errorText,
  actionText,
  mobileImage,
  desktopImage,
  isMobile = false,
}: {
  errorText?: string;
  actionText?: string;
  mobileImage?: string;
  desktopImage?: string;
  isMobile?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();

  const [Container, containerProps] = isMobile
    ? ['section', { css: { padding: theme.spacing(2) } }]
    : [Paper, { css: { component: 'section', padding: theme.spacing(6) } }];

  return (
    <Container {...containerProps}>
      <ResponsiveContainer>
        <img src={iconError} alt="error-icon" />
        <WarningText isMobile={isMobile}>
          {t('challenges.declaration.history.surprised_text')}
        </WarningText>
        {errorText && (
          <ErrorText
            isMobile={isMobile}
            dangerouslySetInnerHTML={{
              __html: errorText,
            }}
          />
        )}
        {actionText && (
          <ActionText
            isMobile={isMobile}
            dangerouslySetInnerHTML={{
              __html: actionText,
            }}
          />
        )}
        {isMobile && mobileImage && (
          <ImgMobile src={mobileImage} alt="down-arrow" />
        )}
      </ResponsiveContainer>
      {!isMobile && desktopImage && (
        <ImgDesktop src={desktopImage} alt="dotted-arrow" />
      )}
    </Container>
  );
}
